import constants from "../../Utils/constants";
import useTranslator from "../../hooks/useTranslator";
import CheckIcon0 from "../../assets/img/checkIcon02.svg";
import Twotone from "../../assets/img/Twotone.svg";
import {
  checkSalary,
  convertCurrencyFormat,
  haveValue,
  getUpdatedExchangeIncome,
} from "../../helper/common";
import { useContext, useEffect, useMemo, useState } from "react";
import moment from "moment";
import AuthContext from "../../context/auth/AuthContext";
import LandLordContext from "../../context/landlord/LandLordContext";
// #NOTE: Modelo 130 (calculateModelo130Income) is kept but not run for now;
// see getModelo130Documents in helper/taxSelection.js
import {
  TAX_VALUE_FIELD,
  getDefaultTaxIds,
  getTaxDocFieldKeys,
  getTaxDocFieldValue,
  getTaxDocTitle,
  getTaxDocuments,
  getTaxFieldId,
  getTaxMonthlyAmount,
  getTaxSelectionMap,
  pickDefaultTaxDoc,
  resolveTaxIds,
} from "../../helper/taxSelection";
import TaxSelectionModal from "./TaxSelectionModal";
import OtherIncomeSelectionModal from "./OtherIncomeSelectionModal";
import {
  getOtherIncomeGroupAmount,
  getOtherIncomeItems,
} from "../../helper/otherIncomeSelection";
import {
  getDefaultPayslipIds,
  getPayslipFieldId,
  getPayslipGroupAmount,
  getPayslipItems,
  groupPayslipsByEmployer,
  parsePayslipFieldEntries,
  findRawPayslip,
  withPayslipAmounts,
  withGrossOnlyPayslips,
  parseComponentModes,
  setComponentMode,
} from "../../helper/payslipSelection";
import PayslipSelectionModal from "./PayslipSelectionModal";
import { Badge, Button, Card, Icon, Notice } from "../design";
import "./tenantInfoCards.css";

const DetailsIncomeInfo = (props) => {
  const {
    user = {},
    forensicAnalysis = null,
    handleInfoModal,
    isFromReport,
    llShowOnly,
    // clubbed payslip ids currently in effect (userIncome.payslip_ids);
    // every change is passed to onPayslipSelectionChange(ids)
    initialSelectedPayslipIds = null,
    onPayslipSelectionChange,
    // figures used per payslip ("<id>::net|gross", userIncome.payslip_fields);
    // every change is passed to onPayslipFieldsChange(entries)
    initialPayslipFields = null,
    onPayslipFieldsChange,
    // used to open payslip documents ("View")
    docsMap = {},
    uploadedTenantDocs = [],
    // clubbed tax field ids currently in effect (userIncome.tax_ids);
    // every change is passed to onTaxSelectionChange(ids)
    initialSelectedTaxIds = null,
    onTaxSelectionChange,
    // clubbed other income ids in effect (userIncome.other_income_ids);
    // every change is passed to onOtherIncomeSelectionChange(ids)
    initialSelectedOtherIncomeIds = null,
    onOtherIncomeSelectionChange,
    // Sum / Mean per component ("payslips:avg", userIncome.component_modes);
    // every change is passed to onComponentModesChange(entries)
    initialComponentModes = null,
    onComponentModesChange,
  } = props;
  const { _t } = useTranslator();
  const { user: originalUser } = useContext(AuthContext);
  const { deaWorkspace } = useContext(LandLordContext);

  const [recordedSalary, setRecordedSalary] = useState({
    salary: 0,
    matched: 0,
  });

  const declaredIncome = getUpdatedExchangeIncome(
    user?.current_annual_income,
    user?.currency,
    originalUser
  );

  useEffect(() => {
    if (haveValue(user)) {
      const temp_sal = checkSalary(user);
      setRecordedSalary(temp_sal);
    }
  }, [user]);

  // items come from tenantExtraData.forensic_payslip_items (stable ids)
  // raw forensic payslip for an item: matched by backend id format, or by
  // the legacy key for items built from forensic_financial_data
  const rawPayslips = forensicAnalysis?.Income?.dataInJSON?.payslips ?? [];
  const getRawPayslip = (item) => findRawPayslip(item, rawPayslips);

  // verified tick uses the same rule as before (payslip source / social
  // security / Income verified) whenever the raw payslip is available
  const isRawPayslipVerified = (p) =>
    Boolean(
      (forensicAnalysis?.Income?.verifiedSource === true && p?.verified) ||
      p?.payslipSourceVerified ||
      (p?.verified && p?.socialSecuritySourceVerified)
    );

  const payslipItems = useMemo(
    () =>
      withGrossOnlyPayslips(
        withPayslipAmounts(getPayslipItems(user), rawPayslips),
        rawPayslips
      ).map((item) => {
        const raw = getRawPayslip(item);
        return raw ? { ...item, verified: isRawPayslipVerified(raw) } : item;
      }),
    [user, forensicAnalysis]
  );
  const payslipEmployerGroups = useMemo(
    () => groupPayslipsByEmployer(payslipItems),
    [payslipItems]
  );
  const effectivePayslipIds = useMemo(
    () =>
      Array.isArray(initialSelectedPayslipIds)
        ? initialSelectedPayslipIds
        : getDefaultPayslipIds(payslipItems),
    [initialSelectedPayslipIds, payslipItems]
  );
  const effectivePayslipFields = useMemo(
    () => (Array.isArray(initialPayslipFields) ? initialPayslipFields : []),
    [initialPayslipFields]
  );
  // payslips of the latest month (across employers) get the Latest tag
  const latestPayslipIds = useMemo(
    () => getDefaultPayslipIds(payslipItems),
    [payslipItems]
  );
  // ---- Sum / Mean of the selected items, per component ----
  const [componentModeEntries, setComponentModeEntries] = useState(
    initialComponentModes ?? []
  );
  // follow the page's value (load / revert after a failed save); compared by
  // content so a new but equal list doesn't reset the state
  const initialComponentModesKey = JSON.stringify(initialComponentModes ?? []);
  useEffect(() => {
    setComponentModeEntries(JSON.parse(initialComponentModesKey));
  }, [initialComponentModesKey]);
  const componentModes = useMemo(
    () => parseComponentModes(componentModeEntries),
    [componentModeEntries]
  );
  // applied instantly by the page and saved in the background there
  const changeComponentMode = (component, mode) => {
    const next = setComponentMode(componentModeEntries, component, mode);
    setComponentModeEntries(next);
    onComponentModesChange?.(next);
  };

  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedPayslipIds, setSelectedPayslipIds] = useState([]);
  const [selectedPayslipFields, setSelectedPayslipFields] = useState([]);

  useEffect(() => {
    setSelectedPayslipIds(effectivePayslipIds);
  }, [effectivePayslipIds]);

  useEffect(() => {
    setSelectedPayslipFields(effectivePayslipFields);
  }, [effectivePayslipFields]);

  const payslipFieldMap = useMemo(
    () => parsePayslipFieldEntries(selectedPayslipFields),
    [selectedPayslipFields]
  );

  // every change is applied instantly by the page (effort rate updates
  // right away) and saved in the background there
  const updatePayslipIds = (next) => {
    setSelectedPayslipIds(next);
    onPayslipSelectionChange?.(next);
  };

  const togglePayslip = (payslipId) =>
    updatePayslipIds(
      selectedPayslipIds.includes(payslipId)
        ? selectedPayslipIds.filter((id) => id !== payslipId)
        : [...selectedPayslipIds, payslipId]
    );

  // net / gross checkboxes of one payslip. Choosing a figure on an unticked
  // payslip ticks it; unticking its last figure unticks the payslip.
  const togglePayslipField = (item, field) => {
    // radio: net OR gross, choosing one also ticks the payslip
    const isSelected = selectedPayslipIds.includes(item.id);
    const nextFields = [field];

    const otherEntries = selectedPayslipFields.filter(
      (entry) => !entry.startsWith(`${item.id}::`)
    );
    const nextEntries = [
      ...otherEntries,
      ...nextFields.map((f) => getPayslipFieldId(item.id, f)),
    ];
    setSelectedPayslipFields(nextEntries);
    onPayslipFieldsChange?.(nextEntries);

    if (!isSelected) updatePayslipIds([...selectedPayslipIds, item.id]);
  };

  // document of a payslip, opened in a new tab (same lookup as DataInfoModal)
  const getPayslipDocumentUrl = (item) => {
    const raw = getRawPayslip(item);
    const docs = raw?.foundInDocuments ?? [];
    const withFile = docs.find((d) => d?.file_url || d?.fileUrl);
    const first = docs[0];
    const url =
      item?.document_url ||
      withFile?.file_url ||
      withFile?.fileUrl ||
      docsMap?.[first?.documentId ?? first?.document_id]?.url ||
      docsMap?.[item?.document_id]?.url;
    if (!url) return null;
    const uploadKey = url.split("uploads/")[1];
    const signed = (uploadedTenantDocs ?? []).find(
      (d) => (d?.file_name || d?.url?.split("/")?.pop()) === uploadKey
    );
    return signed?.url || url;
  };

  const closePayslipModal = () => setShowPayslipModal(false);

  // one line of the income list; without onClick it is automatic (bank).
  // The whole row is clickable with the mouse; the "Change" button inside
  // gives keyboard users the same action (its click bubbles to the row).
  const IncomeRow = ({ label, verified, amount, meta, onClick }) => (
    <div
      className={`tic-income-row ${onClick ? "tic-income-row--click" : ""}`}
      onClick={onClick}
    >
      <span className="tic-income-row__info">
        <span className="tic-income-row__label">
          {label}
          {verified && (
            <img src={CheckIcon0} alt="Verified" className="tic-check" />
          )}
        </span>
        <span className="tic-income-row__value">
          <b>€{convertCurrencyFormat(amount ?? 0)}</b>
          {meta && <span className="tic-income-row__meta">{meta}</span>}
        </span>
      </span>
      {onClick ? (
        <Button
          variant="secondary"
          size="sm"
          iconEnd={<Icon name="chevronRight" size={14} />}
        >
          {_t("change")}
        </Button>
      ) : (
        <Badge tone="neutral">
          <Icon name="lock" size={12} />
          {_t("automatic")}
        </Badge>
      )}
    </div>
  );

  const selectedPayslips = payslipItems.filter((i) =>
    selectedPayslipIds.includes(i.id)
  );
  const selectedPayslipTotal = getPayslipGroupAmount(
    payslipItems,
    selectedPayslipIds,
    originalUser,
    selectedPayslipFields,
    componentModes.payslips
  );

  // ---- clubbed tax ("itr"): one item per tax document (Model 100 / 130 /
  // retired tenants: their payslip entries are pensions, marked "(Pension)"
  const isPensionIncome =
    user?.employment_status === constants.retired ||
    forensicAnalysis?.["Employment Type"]?.dataInJSON?.employmentType ===
      "Retired";
  const pensionMark = isPensionIncome ? ` (${_t("pension")})` : "";

  // 303); monthly total = sum of the selected documents' monthly values ----
  const isDeaTaxRule = Boolean(
    originalUser?.current_workspace?.dea_rules_activated &&
    deaWorkspace &&
    (forensicAnalysis?.["Employment Type"]?.dataInJSON?.employmentType ===
      "Employee" ||
      forensicAnalysis?.["Employment Type"]?.dataInJSON?.employmentType ===
        "Self-employed (Company Owner)")
  );
  const taxDocs = useMemo(
    () => getTaxDocuments(user, forensicAnalysis) ?? [],
    [user, forensicAnalysis]
  );
  // the document the previous hierarchy picks gets the Latest tag
  const defaultTaxDoc = useMemo(
    () => pickDefaultTaxDoc(taxDocs, isDeaTaxRule),
    [taxDocs, isDeaTaxRule]
  );
  const effectiveTaxIds = useMemo(
    () =>
      Array.isArray(initialSelectedTaxIds)
        ? resolveTaxIds(taxDocs, initialSelectedTaxIds, isDeaTaxRule)
        : getDefaultTaxIds(taxDocs, isDeaTaxRule),
    [initialSelectedTaxIds, taxDocs, isDeaTaxRule]
  );
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [selectedTaxIds, setSelectedTaxIds] = useState([]);

  useEffect(() => {
    setSelectedTaxIds(effectiveTaxIds);
  }, [effectiveTaxIds]);

  const taxSelectionMap = useMemo(
    () => getTaxSelectionMap(selectedTaxIds),
    [selectedTaxIds]
  );

  const updateTaxIds = (next) => {
    setSelectedTaxIds(next);
    onTaxSelectionChange?.(next);
  };

  // figure ticked when a document is ticked from the list: the previous
  // default for a Model 100, else its first figure with a value
  const getTaxDocDefaultField = (doc) => {
    if (doc.model !== "100" && doc.model !== "200") return TAX_VALUE_FIELD;
    const fromDefault = getDefaultTaxIds([doc], isDeaTaxRule)[0];
    if (fromDefault) return fromDefault.split(":").pop();
    const keys = getTaxDocFieldKeys(doc, isDeaTaxRule);
    return keys.find((k) => getTaxDocFieldValue(doc, k) > 0) ?? keys[0];
  };

  const toggleTaxDoc = (doc) => {
    const isSelected = (taxSelectionMap[doc.id] ?? []).length > 0;
    updateTaxIds(
      isSelected
        ? selectedTaxIds.filter((id) => !id.startsWith(`${doc.id}:`))
        : [...selectedTaxIds, getTaxFieldId(doc, getTaxDocDefaultField(doc))]
    );
  };

  // a figure ticks its document; unticking the last figure unticks it
  const toggleTaxField = (doc, field) => {
    const fieldId = getTaxFieldId(doc, field);
    updateTaxIds(
      selectedTaxIds.includes(fieldId)
        ? selectedTaxIds.filter((id) => id !== fieldId)
        : [...selectedTaxIds, fieldId]
    );
  };

  const selectedTaxDocs = taxDocs.filter(
    (d) => (taxSelectionMap[d.id] ?? []).length > 0
  );
  const taxMonthlyIncome = getTaxMonthlyAmount(
    taxDocs,
    selectedTaxIds,
    originalUser,
    componentModes.tax
  );

  // document of a tax return, matched in the Tax Returns key factor by
  // model and period; opened in a new tab
  const getTaxDocumentUrl = (doc) => {
    const docs =
      (forensicAnalysis?.["Tax Returns"] ?? forensicAnalysis?.["Tax returns"])
        ?.foundInDocuments ?? [];
    // Company login tenants: Form 200 is the corporate tax return, Form 390
    // the annual VAT summary (their document names have no "Model").
    const modelPattern =
      doc.model === "303"
        ? /303/
        : doc.model === "200"
          ? /Corporate Tax Return|\b200\b/i
          : doc.model === "390"
            ? /390/
            : new RegExp(`Model\\s*${doc.model}`, "i");
    const candidates = docs.filter(
      (d) =>
        modelPattern.test(d?.documentName ?? d?.name ?? "") &&
        Number(d?.period_year) === Number(doc.year)
    );
    const match =
      candidates.find(
        (d) => !doc.quarter || Number(d?.period_quarter) === doc.quarter
      ) ?? candidates[0];
    const url =
      match?.data?.url ||
      match?.url ||
      match?.file_url ||
      docsMap?.[match?.documentId ?? match?.document_id]?.url;
    if (!url) return null;
    const uploadKey = url.split("uploads/")[1];
    const signed = (uploadedTenantDocs ?? []).find(
      (d) => (d?.file_name || d?.url?.split("/")?.pop()) === uploadKey
    );
    return signed?.url || url;
  };

  // ---- clubbed other incomes: sum of chosen baseAmounts (monthly);
  // nothing selected by default ----
  const otherIncomeItems = useMemo(
    () => getOtherIncomeItems(user, forensicAnalysis),
    [user, forensicAnalysis]
  );
  const effectiveOtherIncomeIds = useMemo(
    () =>
      Array.isArray(initialSelectedOtherIncomeIds)
        ? initialSelectedOtherIncomeIds
        : [],
    [initialSelectedOtherIncomeIds]
  );
  const [showOtherIncomeModal, setShowOtherIncomeModal] = useState(false);
  const [selectedOtherIncomeIds, setSelectedOtherIncomeIds] = useState([]);

  useEffect(() => {
    setSelectedOtherIncomeIds(effectiveOtherIncomeIds);
  }, [effectiveOtherIncomeIds]);

  const toggleOtherIncome = (id) => {
    const next = selectedOtherIncomeIds.includes(id)
      ? selectedOtherIncomeIds.filter((x) => x !== id)
      : [...selectedOtherIncomeIds, id];
    setSelectedOtherIncomeIds(next);
    onOtherIncomeSelectionChange?.(next);
  };

  const otherIncomeTotal = getOtherIncomeGroupAmount(
    otherIncomeItems,
    selectedOtherIncomeIds,
    originalUser,
    componentModes.otherIncomes
  );

  const taxKeyFactor =
    forensicAnalysis?.["Tax Returns"] ?? forensicAnalysis?.["Tax returns"];

  return (
    <>
      <Card
        className={`tic-card mt-3 ${
          isFromReport ? "main_doc_div_report main_doc_div_report_full" : ""
        }`}
        title={
          <span className="tic-card__title">
            <img src={Twotone} alt="" className="tic-card__icon" />
            {_t("incomeInformation")}
          </span>
        }
      >
        <div className="row">
          {!llShowOnly && (
            <div className="col-md-4 col-6" style={{ cursor: "pointer" }}>
              <div className="tic-field">
                <p className="tic-field__label">{_t("declaredIncome")}</p>
                <p className="tic-field__value">
                  €{convertCurrencyFormat(declaredIncome ?? 0)}
                </p>
                <p className="tic-field__value tic-field__note">
                  {user?.currency !== "EUR" && `(${_t("converted")})`}
                </p>
              </div>
            </div>
          )}
        </div>
        {llShowOnly &&
          (payslipItems.length > 0 ||
            taxDocs.length > 0 ||
            haveValue(recordedSalary?.salary) ||
            otherIncomeItems.length > 0) && (
            <div className="tic-income-list">
              {payslipItems.length > 0 &&
                (() => {
                  const count = selectedPayslips.length;
                  const employers = new Set(
                    selectedPayslips.map((p) => p?.employer ?? "")
                  ).size;
                  const label = `${
                    count === 1
                      ? `${_t("incomeFromPayslip")} (${moment(
                          selectedPayslips[0]?.month,
                          "YYYY-MM"
                        ).format("MMM YYYY")})`
                      : count > 1
                        ? `${_t("incomeFromPayslip")} (${count} ${_t("payslips")})`
                        : _t("incomeFromPayslip")
                  }${pensionMark}`;
                  return (
                    <IncomeRow
                      label={label}
                      verified={
                        count > 0 && selectedPayslips.every((p) => p?.verified)
                      }
                      amount={selectedPayslipTotal}
                      meta={
                        count === 0
                          ? _t("notSelected")
                          : employers === 1
                            ? _t("fromOneEmployer")
                            : `${_t("from")} ${employers} ${_t("employers")}`
                      }
                      onClick={() => setShowPayslipModal(true)}
                    />
                  );
                })()}
              {taxDocs.length > 0 &&
                (() => {
                  const count = selectedTaxDocs.length;
                  const first = selectedTaxDocs[0];
                  const firstFields = first ? taxSelectionMap[first.id] : [];
                  const label =
                    count === 1
                      ? `${_t("incomeFromTaxReturn")} (${getTaxDocTitle(first, _t)})`
                      : count > 1
                        ? `${_t("incomeFromTaxReturn")} (${count} ${_t("returns")})`
                        : _t("incomeFromTaxReturn");
                  const meta =
                    count > 1
                      ? componentModes.tax === "avg"
                        ? _t(
                            "mean",
                            "Mean option of the Sum / Mean switch in the income panels"
                          )
                        : _t("combinedTotal")
                      : count === 1
                        ? first.model === "100" || first.model === "200"
                          ? firstFields.length === 1
                            ? _t(firstFields[0])
                            : `${firstFields.length} ${_t("figures")}`
                          : first.model === "130"
                            ? _t("netAccumulatedIncome03")
                            : first.model === "390"
                              ? _t("volumenOperaciones99")
                              : _t("totalInvoicing")
                        : _t("notSelected");
                  return (
                    <IncomeRow
                      label={label}
                      verified={count > 0 && taxKeyFactor?.verifiedSource}
                      amount={taxMonthlyIncome}
                      meta={`${meta}${
                        selectedTaxDocs.some(
                          (d) => d?.currency && d.currency !== "EUR"
                        )
                          ? ` (${_t("converted")})`
                          : ""
                      }`}
                      onClick={() => setShowTaxModal(true)}
                    />
                  );
                })()}
              {haveValue(recordedSalary?.salary) && (
                <IncomeRow
                  label={_t("netIncomeFromBank")}
                  verified={
                    haveValue(
                      user?.bank_details?.userDataByProvider?.[0]?.providerName
                    ) &&
                    user?.bank_details?.userDataByProvider?.[0]
                      ?.providerName !== "Uploaded from CSV" &&
                    recordedSalary?.salary > 0
                  }
                  amount={
                    !Number.isNaN(recordedSalary?.salary)
                      ? recordedSalary?.salary
                      : 0
                  }
                  meta={
                    recordedSalary?.salary > 0
                      ? moment(
                          haveValue(user?.bankVerificationDate)
                            ? user?.bankVerificationDate
                            : user?.created_at
                        ).format("MMM YYYY")
                      : null
                  }
                />
              )}
              {otherIncomeItems.length > 0 &&
                (() => {
                  const chosen = otherIncomeItems.filter((i) =>
                    selectedOtherIncomeIds.includes(i.id)
                  );
                  return (
                    <IncomeRow
                      label={`${_t("otherIncomes")}${
                        chosen.length === 1 && chosen[0]?.name
                          ? ` (${chosen[0].name})`
                          : ""
                      }`}
                      amount={otherIncomeTotal}
                      meta={`${chosen.length} ${_t("selected")}`}
                      onClick={() => setShowOtherIncomeModal(true)}
                    />
                  );
                })()}
            </div>
          )}
        <TaxSelectionModal
          isOpen={showTaxModal}
          onClose={() => setShowTaxModal(false)}
          documents={taxDocs}
          selectionMap={taxSelectionMap}
          latestId={defaultTaxDoc?.id ?? null}
          isDeaTaxRule={isDeaTaxRule}
          onToggleDoc={toggleTaxDoc}
          onToggleField={toggleTaxField}
          monthlyTotal={taxMonthlyIncome}
          originalUser={originalUser}
          getDocumentUrl={getTaxDocumentUrl}
          mode={componentModes.tax}
          onModeChange={(m) => changeComponentMode("tax", m)}
          onViewDetails={
            taxKeyFactor
              ? () => {
                  setShowTaxModal(false);
                  handleInfoModal(taxKeyFactor);
                }
              : null
          }
        />
        <PayslipSelectionModal
          isOpen={showPayslipModal}
          onClose={closePayslipModal}
          employerGroups={payslipEmployerGroups}
          selectedIds={selectedPayslipIds}
          fieldMap={payslipFieldMap}
          latestIds={latestPayslipIds}
          onTogglePayslip={togglePayslip}
          onToggleField={togglePayslipField}
          total={selectedPayslipTotal}
          originalUser={originalUser}
          getDocumentUrl={getPayslipDocumentUrl}
          isPension={isPensionIncome}
          mode={componentModes.payslips}
          onModeChange={(m) => changeComponentMode("payslips", m)}
        />
        <OtherIncomeSelectionModal
          isOpen={showOtherIncomeModal}
          onClose={() => setShowOtherIncomeModal(false)}
          items={otherIncomeItems}
          selectedIds={selectedOtherIncomeIds}
          onToggle={toggleOtherIncome}
          total={otherIncomeTotal}
          originalUser={originalUser}
          mode={componentModes.otherIncomes}
          onModeChange={(m) => changeComponentMode("otherIncomes", m)}
        />
        {llShowOnly &&
          (() => {
            const hasBankIncome = haveValue(recordedSalary?.salary);
            const hasItrIncome = taxDocs.length > 0;
            const hasPayslips =
              payslipItems.length > 0 ||
              (haveValue(forensicAnalysis?.Income?.dataInJSON?.payslips) &&
                forensicAnalysis?.Income?.dataInJSON?.payslips?.length > 0);
            const hasVerifiedSource =
              hasBankIncome ||
              hasItrIncome ||
              hasPayslips ||
              otherIncomeItems.length > 0;

            if (hasVerifiedSource) return null;

            return (
              <Notice>
                {_t(
                  "noVerifiedIncomeSourcesFound",
                  "Placeholder shown in monthly income detail view for landlords when no verified income sources exist"
                )}
              </Notice>
            );
          })()}
      </Card>
    </>
  );
};

export default DetailsIncomeInfo;
